import { Box, Paper, Skeleton, Typography } from '@mui/material';
import MainLogo from 'assets/main-logo.png';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { IRevisionWithProjectResponse } from 'utils/contracts';
import { formatClimate, version_options } from 'utils/Formatter';

interface ProjectBannerProps {
  revisionWithProjectData?: IRevisionWithProjectResponse;
  isLoading: boolean;
  headerActions?: React.ReactNode;
}

const ProjectBanner: React.FC<ProjectBannerProps> = ({ revisionWithProjectData, isLoading, headerActions }) => {
  const { t } = useTranslation();



  // Helper function to format date as DD/MM/YYYY
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <Paper
        elevation={0}
        dir="ltr"
        sx={{
          position: 'absolute',
          top: 5,
          left: 0,
          right: 0,
          zIndex: 10,
          background: 'white',
          borderRadius: 0,
          height: '60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: 'none',
        }}
      >
        {/* Left side - Logo skeleton */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Skeleton variant="rectangular" width={120} height={32} />
        </Box>
        {/* Right side - Project information skeleton */}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Box sx={{ display: 'flex', gap: '2rem' }}>
            {[1, 2, 3, 4, 5].map((index) => (
              <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <Skeleton variant="text" width={120} height={24} />
                <Skeleton variant="text" width={80} height={16} />
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
    );
  }

  // Return early if no data
  if (!revisionWithProjectData) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        background: 'white',
        gap: '2rem',
        borderRadius: 0,
        padding: '1rem 1rem',
        height: '80px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: 'none',
      }}
      dir="ltr"
    >
      {/* Left side - Logo */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box>
          <img
            src={MainLogo}
            alt="DesignBuilder Logo"
            className="h-8"
            style={{ width: 'auto', display: 'block' }}
          />
        </Box>
      </Box>

      {/* Right side - Project information columns */}
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
        <Box sx={{ display: 'flex',  gap: '2rem'  }}>
          {/* Column 1: Project Name + Revision ID */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <Typography
              variant="body1"
              sx={{
                color: '#1f2937',
                fontSize: '18px',
                fontWeight: 600,
                fontFamily: 'Assistant, sans-serif',
              }}
            >
              {revisionWithProjectData.organization_name}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                color: '#6b7280',
                fontWeight: 400,
                fontFamily: 'Assistant, sans-serif',
              }}
            >
              {revisionWithProjectData.revision_id}
            </Typography>
          </Box>

          {/* Column 2: Project Type + Construction */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <Typography
              variant="body1"
              sx={{
                color: '#1f2937',
                fontSize: '18px',
                fontWeight: 600,
                fontFamily: 'Assistant, sans-serif',
              }}
            >

              {revisionWithProjectData.project_name}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                color: '#6b7280',
                fontWeight: 400,
                fontFamily: 'Assistant, sans-serif',
              }}
            >
              {revisionWithProjectData.project_construction?.charAt(0).toUpperCase() + 
                revisionWithProjectData.project_construction?.slice(1)}
              {' '} 
               {revisionWithProjectData.project_type}
            </Typography>
          </Box>

          {/* Column 3: Climate + Terrain */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <Typography
              variant="body1"
              sx={{
                color: '#1f2937',
                fontSize: '18px',
                fontWeight: 600,
                fontFamily: 'Assistant, sans-serif',
              }}
            >
              {t(formatClimate(revisionWithProjectData.project_climate), { lng: 'en' })}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                color: '#6b7280',
                fontWeight: 400,
                fontFamily: 'Assistant, sans-serif',
              }}
            >
              {revisionWithProjectData.project_terrain?.charAt(0).toUpperCase() + 
                revisionWithProjectData.project_terrain?.slice(1)
              }
            </Typography>
          </Box>

          {/* Column 4: Rating + Grade */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <Typography
              variant="body1"
              sx={{
                color: '#1f2937',
                fontSize: '18px',
                fontWeight: 600,
                fontFamily: 'Assistant, sans-serif',
              }}
            >
              {revisionWithProjectData.revision_rating}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                color: '#6b7280',
                fontWeight: 400,
                fontFamily: 'Assistant, sans-serif',
              }}
            >
              {revisionWithProjectData.revision_weighed_grade}
            </Typography>
          </Box>

          {/* Column 5: Date + Version */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <Typography
              variant="body1"
              sx={{
                color: '#1f2937',
                fontSize: '18px',
                fontWeight: 600,
                fontFamily: 'Assistant, sans-serif',
              }}
            >
              {formatDate(revisionWithProjectData.revision_updatedAt)}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.875rem',
                color: '#6b7280',
                fontWeight: 400,
                fontFamily: 'Assistant, sans-serif',
              }}
            >
              {version_options.find(option => option.value === revisionWithProjectData.project_version)?.label}
            </Typography>
          </Box>
        </Box>
      </Box>

      {headerActions && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexShrink: 0,
            ml: 2,
          }}
        >
          {headerActions}
        </Box>
      )}
    </Paper>
  );
};

export default ProjectBanner; 